// Em que fatura cai uma compra de cartão — e quando ela vence.
//
// Pedido da Daniele (31/08/2026): ao lançar uma despesa paga com cartão, o
// vencimento deve vir sozinho do ciclo do cartão, em vez de ela calcular de
// cabeça a cada lançamento.
//
// ⚠️ A REGRA É A DO SERVIDOR — dre-engine/handlers/cartoes.js (offset1 /
// offsetVenc, POST /financeiro/cartoes/{id}/compras). Este arquivo é a cópia
// para a tela sugerir na hora da digitação; se a regra mudar lá, muda aqui.
//
//   · compra até o dia do FECHAMENTO entra no ciclo do próprio mês; depois
//     disso, no ciclo do mês seguinte (offset1);
//   · cartão que vence no mês seguinte ao fechamento (venc <= fech, ex.
//     fecha 25 / vence 5) desloca o vencimento +1 mês (offsetVenc);
//   · dia inexistente no mês (31 em fevereiro) recua para o último dia.

export interface CicloCartao {
  dia_fechamento?: number | null;
  dia_vencimento?: number | null;
}

function dataNoMes(y: number, m: number, dia: number): string {
  const ultimo = new Date(Date.UTC(y, m, 0)).getUTCDate();
  const d = Math.min(Math.max(1, dia), ultimo);
  return `${y}-${String(m).padStart(2, '0')}-${String(d).padStart(2, '0')}`;
}

function addMeses(y: number, m: number, delta: number): { y: number; m: number } {
  const total = (m - 1) + delta;
  return { y: y + Math.floor(total / 12), m: ((total % 12) + 12) % 12 + 1 };
}

export interface FaturaSugerida {
  vencimento: string;    // YYYY-MM-DD — quando a fatura desta compra vence
  fechamento: string;    // YYYY-MM-DD — quando o ciclo desta compra fecha
}

/**
 * Dada a data da compra e o ciclo do cartão, devolve o vencimento da fatura em
 * que a compra cai. `null` quando o cartão não tem os dois dias configurados —
 * aí a tela não adivinha: avisa que falta configurar.
 */
export function faturaDaCompra(dataCompra: string, cartao: CicloCartao): FaturaSugerida | null {
  if (!/^\d{4}-\d{2}-\d{2}$/.test(dataCompra || '')) return null;
  const fech = Number(cartao?.dia_fechamento);
  const venc = Number(cartao?.dia_vencimento);
  if (!Number.isFinite(fech) || fech < 1 || !Number.isFinite(venc) || venc < 1) return null;

  const y0 = parseInt(dataCompra.slice(0, 4), 10);
  const m0 = parseInt(dataCompra.slice(5, 7), 10);
  const diaCompra = parseInt(dataCompra.slice(8, 10), 10);

  const offset1 = diaCompra <= fech ? 0 : 1;          // em que ciclo a compra entra
  const offsetVenc = venc <= fech ? 1 : 0;            // o vencimento é no mês seguinte?

  const mesFech = addMeses(y0, m0, offset1);
  const mesVenc = addMeses(y0, m0, offset1 + offsetVenc);
  return {
    fechamento: dataNoMes(mesFech.y, mesFech.m, fech),
    vencimento: dataNoMes(mesVenc.y, mesVenc.m, venc),
  };
}

/** "10/09" a partir de "2026-09-10" — para o texto explicativo da tela. */
export function diaMes(iso: string): string {
  return `${iso.slice(8, 10)}/${iso.slice(5, 7)}`;
}
